/* eslint-disable react/jsx-one-expression-per-line */
import React, { PureComponent } from 'react';
import { Helmet } from 'react-helmet';
import Router from 'next/router';
import { Button, Layout, Typography } from 'antd';
import styled from 'styled-components';

const { Header, Content, Footer } = Layout;
const { Title } = Typography;

const TermsPageWrapper = styled(Layout)`
  min-height: 100vh;

  .header {
    height: 250px;
    display: flex;
    align-items: center;
    background-color: none;
    background: linear-gradient(0deg, rgba(20, 56, 168, 0.75), rgba(20, 56, 168, 0.75)),
      url('https://5startelemed.nyc3.digitaloceanspaces.com/production/static/auth-background.jpg');
    background-size: cover;
    background-position: center;
  }

  .title {
    color: #fff;
    margin: 0;
    letter-spacing: 0.1em;
  }

  .back-btn {
    &:hover {
      border-color: #fff;
      color: #fff;
    }
  }

  .terms {
    background: #fff;
    padding-bottom: 5rem;
  }

  .addendum {
    margin: 5rem 0;
  }

  .container {
    width: 90%;
    align-self: center;
    margin: auto;

    @media (min-width: 576px) {
      max-width: 540px;
    }

    @media (min-width: 768px) {
      max-width: 720px;
    }
  }

  .content {
    .last-updated {
      margin-top: 2rem;
      text-align: right;
    }

    .section-title {
      margin-top: 2em;
      margin-bottom: 1.75rem;
      color: #4260ba;
    }

    .subsection {
      padding-left: 2rem;
    }

    p {
      font-size: 1rem;
      line-height: 1.75em;
      text-align: justify;
      margin-bottom: 1.75rem;
    }

    p:last-child {
      margin-bottom: 0;
    }

    li {
      font-size: 1rem;
      line-height: 1.75em;
      text-align: justify;
      margin-bottom: 1em;
    }

    .well {
      padding: 1.5rem;
      background-color: rgba(20, 56, 168, 0.1);
      border-radius: 2px;
      margin-bottom: 1.75rem;
      font-size: 1rem;

      p {
        margin-bottom: 1rem;
      }

      p:last-child {
        margin-bottom: 0;
      }
    }
  }

  .footer {
    background: #4260ba;
    color: #fff;
    text-align: center;
  }
`;

export default class TermsPage extends PureComponent {
  render() {
    return (
      <TermsPageWrapper>
        <Helmet title="Terms of Use and Privacy Policy | 5-Star Telemed" />
        <Header className="header">
          <div className="container">
            <Title className="title">Terms of Use and Privacy Policy</Title>
            <Button className="back-btn" ghost icon="left" onClick={() => Router.back()} />
          </div>
        </Header>
        <Content className="terms">
          <div className="container">
            <div className="content">
              <p className="last-updated">Last updated: November 5, 2020</p>
              <Title className="section-title" level={3}>
                Introduction
              </Title>
              <p>
                Theoria Medical PLLC builds technologies and services that enable its employees and
                client organizations to provide better patient care by improving workflow and
                efficiency through technology. These terms govern your use of 5-Star Telemed web and
                mobile applications. These terms may change as our business changes and you agree
                that you will review it and any updates regularly.
              </p>
              <p>
                Access to the 5-Star Telemed Content is provided solely to facilitate access to
                5-Star Telemed Services. Subject to compliance with these Terms of Use and Privacy
                Policy and any agreement that you may have executed with Theoria Medical PLLC (“
                <b>Your Agreement</b>”), your access to the 5-Star Telemed services and 5-Star
                Telemed content is on a limited, non-exclusive, non-transferable basis only during
                the term of Your Agreement.
              </p>
              <p>
                <b>Revisions to Terms of Use and Privacy Policy.</b> We may revise these Terms of
                Use at any time by posting an updated version to this web page. You should visit
                this page periodically to review the most current Terms of Use and Privacy Policy
                because you are bound by them when you use the 5-Star Telemed services as well as
                our website. Your continued use of the 5-Star Telemed services after a change to
                these Terms of Use and Privacy Policy constitutes your binding acceptance of these
                Terms of Use and Privacy Policy.
              </p>

              <p>
                <b>&quot;Health-related information&quot;</b> means all individually identifiable
                health information, including demographic information and information related to
                individual’s physical or mental health, the provision of health care to the
                individual, or the individual’s payment for the provision of health care.
              </p>

              <Title className="section-title" level={3}>
                The 5-Star Telemed Services
              </Title>

              <p>
                The&nbsp;“<b>5-Star Telemed Services</b>”&nbsp;are defined as any computer or mobile
                application or software under 5-Star Telemed’s control, whether partial or
                otherwise, in connection with providing the services provided by 5-Star Telemed,
                including any website or application owned or controlled by 5-Star Telemed. The
                5-Star Telemed Services include but are not limited to, providing a platform to
                offer telemedicine services, and providing a platform to store health-related
                information to make it available to you and your employees.
              </p>

              <p>
                <b>Third-Party Services.</b> Your use of the 5-Star Telemed Services may include
                certain third-party software and services or allow you to access additional software
                and services offered by third-parties. You agree to comply with any additional terms
                required by our third-party vendors and suppliers, as necessary, to use parts of the
                5-Star Telemed Services.
              </p>
              <div className="well">
                <p>
                  You may allow employees to use the 5-Star Telemed Services on your behalf. You may
                  allow patients to use the 5-Star Telemed Services to pay for your services.
                </p>
                <p>
                  You are responsible for the use and misuse of the 5-Star Telemed Services by the
                  employees and patients you allow to access the 5-Star Telemed Services using your
                  account.
                </p>
              </div>

              <p>
                <b>No Third-Party Access.</b> Except as required by law and described below, you
                will not permit any third party to have access to the 5-Star Telemed Services using
                your account. You may allow your employees to use the 5-Star Telemed Services on
                your behalf in compliance with these Terms of Use. You may also allow patients to
                make payment for your services through the 5-Star Telemed Services in compliance
                with these Terms of Use. You are solely responsible for the use of the 5-Star
                Telemed Services by your employees and patients. We have no liability for the
                consequences to you or your employees or patients from your or their use of the
                5-Star Telemed Services.
              </p>
              <p>
                <b>Responsibility for Misuse by Other Users of Your Account.</b> You acknowledge
                that by allowing employees and patients to access the 5-Star Telemed Services with
                your account, you are responsible for ensuring such employees and patients use the
                5-Star Telemed Services for the purposes for which they are accessing it. While the
                5-Star Telemed Services has certain technical safeguards against misuse, you
                acknowledge it will rely to a substantial extent on your responsible use. You agree
                that we will not be responsible for any unlawful access to or use of the 5-Star
                Telemed Services by any employee or patient to whom you gave access or log-in
                credentials. You agree that you are responsible for ensuring that health-related
                information is properly protected under applicable law.
              </p>
              <div className="well">
                You represent and warrant that you received any necessary consents from individuals
                before posting their information in the 5-Star Telemed Services.
              </div>

              <p>
                <b>Individuals’ Consent for Data Collection and Use.</b> You represent and warrant
                that you have obtained the proper consents, authorizations, and releases from
                individuals to the fullest extent required by applicable law before posting their
                health-related information to the 5-Star Telemed Services or initiating any payment
                transactions.
              </p>
              <p>
                <b>Exporting Information from the 5-Star Telemed Services.</b> You are solely
                responsible for any health-related information exported from the 5-Star Telemed
                Services by you or the employees and patients that you allow access from your
                account. You represent and warrant that you will export and subsequently use
                protected health information only as permitted by HIPAA and applicable federal and
                state law.
              </p>
              <p>
                <b>Transferring Information into the 5-Star Telemed Services.</b> You may be able to
                transfer information from your or third-party data sources into the 5-Star Telemed
                Services. You represent and warrant that you have all necessary patient
                authorization for such data transfers and that such data transfers will comply with
                HIPAA and other applicable federal and state law.
              </p>
              <p>
                <b>Training and Compliance.</b> You agree to train all employees on the use of the
                5-Star Telemed Services, HIPAA or health privacy obligations, and the requirements
                of these Terms of Use and ensure that they comply with such requirements.
              </p>
              <p>
                <b>Compulsory Information Disclosure.</b> You will promptly notify us of any order
                or demand for compulsory disclosure of health-related information if the disclosure
                requires access to or use of the 5-Star Telemed Services. You will cooperate fully
                with us in connection with any such demand.
              </p>
              <p>
                <b>Data Retention.</b> You are responsible for complying with all state and federal
                laws related to retention of medical data and records, patient access and amendment
                to information, and patient authorization to release data.
              </p>

              <Title className="section-title" level={3}>
                Protected Health Information Practices
              </Title>

              <div className="well">
                When we maintain or process protected health information, we comply with HIPAA and a
                Business Associate we have formed with you.
              </div>
              <p>
                You agree that our practices with respect to protected health information are
                governed by the Health Insurance Portability and Accountability Act of 1996, and any
                amendments and regulations promulgated therefrom (“<b>HIPAA</b>”). By agreeing to
                these Terms of Use, you also agree to 5-Star Telemed’s Business Associate Addendum,
                available&nbsp;
                <u>
                  <a href="#">here</a>
                </u>
                &nbsp;and incorporated by reference into these Terms of Use, unless you or your
                organization have agreed to a different business associate agreement with 5-Star
                Telemed that applies to your use of the 5-Star Telemed Services.
              </p>
              <p>
                You agree to use the 5-Star Telemed Services only as permitted by HIPAA, the 21st
                Century Cures Act, and other applicable data privacy laws.
              </p>
              <p>
                As part of our internal compliance control, to the extent 5-Star Telemed uses third
                party business processing outsourcing vendors in connection with our provision of
                the 5-Star Telemed Services, we reserve the right to conduct an audit of their
                operations in our discretion.
              </p>
              <p>
                <b>Patient-Collected Data</b>. For the avoidance of doubt, each of 5-Star Telemed,
                You, as well as each patient/end-user of Yours (a “<b>Patient</b>”) and 5-Star
                Telemed Inc. (the entity) shall have rights to use the Patient-Collected Data. As
                used herein, the “<b>Patient-Collected Data</b>” shall mean the PHI that is
                collected in connection with Your use of the 5-Star Telemed Services. In the event
                that Patient requests its Patient-Collected Data from 5-Star Telemed whether during
                the then applicable Contract Term (as defined in Your Agreement) or after the
                expiration or termination of this Agreement, 5-Star Telemed shall use its reasonable
                efforts to provide the Patient-Collected Data to a Patient. As used herein, “
                <b>PHI</b>” shall mean individually identifiable health information, as that term is
                defined in 45 C.F.R. § 160.103.
              </p>
              <p>
                <b>Physician-Created Data</b>. Further, nothing herein these Terms of Use shall
                prohibit 5-Star Telemed from using the aggregated, non-personally identifiable data
                created, compiled, analyzed or otherwise collected by the Authorized User in its use
                of the 5-Star Telemed Services or the 5-Star Telemed Content (“<b>Physician Data</b>
                ”). Additionally, 5-Star Telemed shall have the right to either provide or not
                provide the Physician Data to any third parties.
              </p>

              <p>
                <b>Automatically Collected Data</b>. We may collect certain information
                automatically when you use the 5-Star Telemed Services, such as your Internet
                protocol (IP) address, device and advertising identifiers, browser type, operating
                system, Internet service provider, pages that you visit before and after using the
                5-Star Telemed Services, the date and time of a Patient’s visit, information about
                the links you click and pages you view within the 5-Star Telemed Services, and other
                standard server log information.&nbsp; We may also collect certain location
                information when you use our 5-Star Telemed Services, such as your computer’s IP
                address, your mobile device’s GPS signal, or information about nearby WiFi access
                points and cell towers.
              </p>
              <p>
                We may use cookies, pixel tags, Local Shared Objects, and similar technologies to
                automatically collect this information.&nbsp; Cookies are small bits of information
                that are stored by your computer’s web browser.&nbsp; Pixel tags are very small
                images or small pieces of data embedded in images, also known as “web beacons” or
                “clear GIFs,” that can recognize cookies, the time and date a page is viewed, a
                description of the page where the pixel tag is placed, and similar information from
                your computer or device.&nbsp; Local Shared Objects (sometimes referred to as “Flash
                Cookies”) are similar to standard cookies except that they can be larger and are
                downloaded to a computer or mobile device by the Adobe Flash media player.&nbsp; By
                using the 5-Star Telemed Services, you consent to our use of cookies and similar
                technologies.
              </p>
              <p>
                We may also collect technical data to address and fix technical problems and improve
                our 5-Star Telemed Services, including the memory state of your device when a system
                or app crash occurs while using the 5-Star Telemed Services.&nbsp; Your device or
                browser settings may permit you to control the collection of this technical
                data.&nbsp; This data may include parts of a document you were using when a problem
                occurred, or the contents of your communications. By using the 5-Star Telemed
                Services, you are consenting to the collection of this technical data.
              </p>

              <Title className="section-title" level={3}>
                Service Access and Secure Credentials
              </Title>

              <div className="well">
                <p>
                  You are responsible for your log-in credentials and for keeping your account
                  information accurate.
                </p>
                <p>
                  You are responsible for any activity resulting from the use of your log-in
                  credentials on the 5-Star Telemed Services.
                </p>
              </div>

              <p>
                <b>Owner of Account and Health-Related Information.</b> The primary account holder
                (i.e., health service provider) holds the legal rights to the 5-Star Telemed
                Services account and the information posted to the 5-Star Telemed Services by your
                business entity.
              </p>
              <p>
                <b>Accurate Account Information.</b> You represent and warrant that the information
                you provide to 5-Star Telemed upon registration and at all other times will be true,
                accurate, current, and complete. You also represent and warrant that you will ensure
                that this information is kept accurate and up-to-date at all times.
              </p>
              <p>
                <b>Verification.</b> You agree that we may take steps to verify your identity and
                credentials as a health service provider at any time. You agree that we may use and
                disclose information, including “Confidential Information,” about you for such
                purposes, including making inquiry of third parties concerning your identity and
                professional and practice credentials. You authorize such third parties to disclose
                to us such information as we may request for such purposes, and you agree to hold
                them and us harmless from any claim or liability arising from the request for or
                disclosure of such information. You agree that we may terminate your access to the
                5-Star Telemed Services at any time if we are unable at any time to determine or
                verify your qualifications or credentials.
              </p>
              <p>
                <b>Protecting Your Log-In Credentials.</b> As a registered user, you will have
                log-in information, including a username and password. Your account is personal to
                the primary account holder, and you may not share your account and log-in
                information with, or allow access to your account by, any third party. As you will
                be responsible for all activity that occurs under your account and access
                credentials, you should take care to preserve the confidentiality of your username
                and password, and any device that you use to access the 5-Star Telemed Services.
                Your access to the Site may be revoked by 5-Star Telemed at any time with or without
                cause. You agree to defend, indemnify and hold 5-Star Telemed harmless from and
                against all third party claims, damages and expenses (including reasonable
                attorney’s fees) against or incurred by 5-Star Telemed arising out of your breach of
                these Terms of Use or violation of applicable law, your use or access of the Sites,
                or access by anyone accessing the Site using your username and password.
              </p>
              <p>
                <b>Notification in Case of Breach.</b> You agree to notify us immediately of any
                breach in secrecy of your log-in information. If you have any reason to believe that
                your account information has been compromised or that your account has been accessed
                by a third party, you agree to immediately notify&nbsp;
                <a href="/submit-issue">
                  <u>5-Star Telemed support</u>
                </a>
                . You will be solely responsible for the losses incurred by 5-Star Telemed and
                others (including patients) due to any unauthorized use of your account that takes
                place prior to notifying 5-Star Telemed that your account has been compromised.
              </p>
              <p>
                <b>Additional Use Terms.</b> You shall access the 5-Star Telemed Services and 5-Star
                Telemed Content only: (i) through Your Authorized Users acting within the scope of
                their service for You; (ii) on 5-Star Telemed’s servers as authorized by 5-Star
                Telemed; (iii) for Your internal use; and (iv) from and within the United States.
                The 5-Star Telemed Services are not intended to be used outside of the United
                States. 5-Star Telemed makes no representations and warranties that the 5-Star
                Telemed Services comply with applicable law in your jurisdiction and 5-Star Telemed
                shall not be responsible for Your use of the 5-Star Telemed Services outside of the
                United States. In the event that You use the 5-Star Telemed Services outside of the
                United States, you acknowledge and understand that you are solely responsible for
                any and all legal consequences for violating applicable laws within your
                jurisdiction and that You shall have no right of recourse against 5-Star Telemed. If
                You are an RCM Client, You shall not use, directly or indirectly, any patient
                service-related billing system or method other than the 5-Star Telemed Services and
                5-Star Telemed Content, including cash billing systems, unless You (i) use a
                different tax identification number for claims submitted through a different billing
                system, or (ii) agree to use 5-Star Telemed’s mixed remittance process with respect
                to such claims.
              </p>
              <p>
                As used herein, “<b>Authorized Users</b>” shall mean those users (i) who are
                designated by You on the 5-Star Telemed Services control screens and who are (A)
                employees of Yours, or (B) other individuals, corporations, or entities that are
                not, and are not affiliated with, third party payers or competitors of 5-Star
                Telemed, and have a valid HIPAA business associate agreement or other agreement with
                You, and (ii) who have been granted access to the 5-Star Telemed Services and/or
                5-Star Telemed Content by You in Your exercise of reasonable discretion relating to
                the receipt of the 5-Star Telemed Services and/or 5-Star Telemed Content hereunder
                by You, and (iii) from whom You have obtained reasonable assurances that they will
                comply with the access and use and confidentiality terms in these Terms of Use and
                Your Agreement.
              </p>
              <p>
                You shall ensure that each Authorized User complies with these Terms of Use and Your
                Agreement as well as applicable law. You shall terminate any Authorized User’s
                access to the 5-Star Telemed Services or the 5-Star Telemed Content: (i) when such
                person no longer meets the definition of “Authorized User”; (ii) if conduct by such
                Authorized User breaches any term of these Terms of Use and/or Your Agreement; or
                (iii) upon such Authorized User’s indictment, arrest, or conviction of any crime
                related to claims or other transactions, financial relationships, or financial
                dealings in connection with health care, and You shall immediately inform 5-Star
                Telemed of any such indictment, arrest, or conviction. You are responsible for all
                acts and omissions of any Authorized User in connection with such Authorized User’s
                access and use of the 5-Star Telemed Services. 5-Star Telemed may restrict, suspend,
                or terminate an Authorized User’s access to the 5-Star Telemed Services or the
                5-Star Telemed Content if 5-Star Telemed determines in its sole discretion that such
                access has an adverse effect on 5-Star Telemed.
              </p>
              <p>
                Violations of system or network security may result in civil or criminal liability.
                5-Star Telemed will investigate occurrences that may involve such violations and may
                involve, and cooperate with, law enforcement authorities in prosecuting users who
                are involved in such violations. You agree not to use any device, software or
                routine to interfere or attempt to interfere with the proper working of the Sites or
                any activity being conducted on the Sites.
              </p>
              <p>
                Except as expressly permitted under these Terms of Use, You shall not and shall
                cause Your Authorized Users not to: (i) access or use the 5-Star Telemed Services or
                the 5-Star Telemed Content in connection with the provision of any services to third
                parties; (ii) resell, rent, license, lease, provide service bureau or timeshare
                services, transfer, encumber, copy, distribute, publish, exhibit, transmit or
                otherwise make available to any third party any 5-Star Telemed Content or the 5-Star
                Telemed Services; (iii) derive specifications from, reverse engineer, reverse
                compile, disassemble, translate, record, or create derivative works based on the
                5-Star Telemed Services or the 5-Star Telemed Content; (iv) use the 5-Star Telemed
                Services or the 5-Star Telemed Content in a manner that delays, impairs, or
                interferes with system functionality for others or that compromises the security or
                integrity of any data, equipment, software, or system input or output, including but
                not limited to introduction of any viruses or malware into the 5-Star Telemed
                Services; (v) enter data in the 5-Star Telemed Services that is threatening,
                harmful, lewd, offensive, defamatory, or that injures or infringes the rights of
                others; (vi) apply systems to extract or modify information in the 5-Star Telemed
                Services or the Sites using technology or methods such as those commonly referred to
                as “web scraping,” “data scraping,” or “screen scraping” or other automated means of
                any kind to access or copy data on the Sites, deep-link to any feature or content on
                the Sites, bypass our robot exclusion headers or other measures we may use to
                prevent or restrict access to the Sites; (vii) use the 5-Star Telemed Services or
                the 5-Star Telemed Content or any part or aspect thereof for any unlawful purpose or
                to mislead or harass anyone; or (viii) use the 5-Star Telemed Services or the 5-Star
                Telemed Content except as specifically permitted under this Agreement. Use of or
                access to the 5-Star Telemed Services or the 5-Star Telemed Content not in
                accordance with these Terms of Use or Your Agreement is strictly prohibited. Any
                violation will cause 5-Star Telemed irreparable and immediate harm, and 5-Star
                Telemed is entitled to injunctive relief to prevent such violation.
              </p>
              <p>
                <b>Third-Party Browser Extensions.</b> Extensions are small software programs,
                developed by third parties, that can modify and enhance the functionality of Your
                browser. Extensions may have privileges, including the ability to read, record
                and/or modify Your private data, including PHI. These extensions are installed by
                individual users into the browser on their computers and are utilized at users’ own
                risk. Further, such extensions are not affiliated with 5-Star Telemed and 5-Star
                Telemed does not have visibility into which extensions any user may use. 5-Star
                Telemed assumes no risk of loss of data or breach of such data due to Your use of
                browser extensions. 5-Star Telemed makes no representations whatsoever about any
                other website that you may access through its Sites. When you access a non-5-Star
                Telemed site, please understand that it is independent of 5-Star Telemed, and that
                5-Star Telemed has no control over the content on that website. In addition, a link
                to a non-5-Star Telemed website does not mean that 5-Star Telemed endorses or
                accepts any responsibility for the content, or the use, of the linked site. It is up
                to you to take precautions to ensure that whatever you select for your use or
                download is free of such items as viruses, worms, Trojan horses, and other items of
                a destructive nature. If you decide to access any of the third-party sites linked to
                the Sites, you do this entirely at your own risk.
              </p>
              <p>
                Prior to using the 5-Star Telemed Services, if You have one (or more) of these
                extensions enabled in Your browsers, 5-Star Telemed recommends completely removing
                all of these extensions immediately as disabling the extensions may not be
                sufficient to protect Your PHI.&nbsp; We recommend that You only access the 5-Star
                Telemed Services from supported browsers that have all plugins and extensions
                removed.
              </p>
              <p>
                Further, installing any third-party software on Your operating system may also
                subject You to the same risks as using browser extensions. 5-Star Telemed assumes no
                risk of loss of data or breach of such data due to your use of browser extensions.
                5-Star Telemed has no liability to You due to damages caused by any third-party
                software, including, without limitations, browser extensions.
              </p>

              <Title className="section-title" level={3}>
                Communications
              </Title>

              <div className="well">
                5-Star Telemed may communicate with you by email or by posting notice on the 5-Star
                Telemed Services. You may request that we provide notice of security breaches in
                writing.
              </div>
              <p>
                You agree to receive texts, phone calls, and/or email from us at the phone numbers
                and email addresses you provided to us for informational and customer
                service-related purposes.
              </p>
              <p>
                <b>Electronic Notices.</b> By using the 5-Star Telemed Services or providing
                personal information to us, you agree that we may communicate with you
                electronically regarding security, privacy, and administrative issues relating to
                your use of the 5-Star Telemed Services. If we learn of a security system’s breach,
                we may attempt to notify you electronically by posting a notice on the 5-Star
                Telemed Services or sending a text or email to you. You may have a legal right to
                receive this notice in writing. To receive free written notice of a security breach
                (or to withdraw your consent from receiving electronic notice), please&nbsp;
                <u>
                  <a href="#">contact us</a>
                </u>
                .
              </p>
              <Title className="section-title" level={3}>
                5-Star Telemed’s Content Ownership and Use
              </Title>
              <div className="well">
                5-Star Telemed owns all of the content we create, but you may use it while you use
                the 5-Star Telemed Services. You cannot use our logo without our written permission.
              </div>
              <p>
                The contents of the 5-Star Telemed Services include: the 5-Star Telemed websites,
                the 5-Star Telemed Services, materials, 5-Star Telemed Services descriptions,
                proprietary methods, templates, spreadsheets, databases and other electronic tools
                created or owned by 5-Star Telemed, including all data and information included in
                or entered into the 5-Star Telemed Services that 5-Star Telemed compiles, sorts,
                integrates, normalizes, analyzes, maps, stores, processes, selectively aggregates
                and/or combines with multiple disparate data sources, for comparison, benchmarking
                or other similar and lawful purposes, as well as edits, improvements, additions,
                modifications, interfaces, and derivative works prepared from or relating to any of
                the foregoing, and any and all tangible and intangible works of authorship,
                copyrights, patents, trademarks, trade secrets and trade dress, and all intellectual
                property rights in any of the foregoing (collectively,&nbsp;“
                <b>5-Star Telemed Content</b>”). All 5-Star Telemed Content and the compilation
                (meaning the collection, arrangement, and assembly) of all 5-Star Telemed Content
                are the property of 5-Star Telemed or its licensors and are protected under
                copyright, trademark, and other laws
              </p>
              <p>
                <b>License to You.</b> We authorize you, subject to these Terms of Use, to access
                and use the 5-Star Telemed Services and the 5-Star Telemed Content solely for the
                use of the 5-Star Telemed Services, at our discretion. Any other use is expressly
                prohibited. This license is revocable at any time without notice and with or without
                cause. Unauthorized use of the 5-Star Telemed Content may violate copyright,
                trademark, and applicable communications regulations and statutes and is strictly
                prohibited. You must preserve all copyright, trademarks, service marks, and other
                proprietary notices contained in the original 5-Star Telemed Content on any copy you
                make of the 5-Star Telemed Content.
              </p>
              <p>
                <b>5-Star Telemed Marks.</b> 5-Star Telemed, the 5-Star Telemed logo, and other
                5-Star Telemed logos and product and service names are or may be trademarks of
                5-Star Telemed (the&nbsp;“
                <b>5-Star Telemed Marks</b>”). Without our prior written permission, and except as
                solely enabled by any link as provided by us, you agree not to display or use in any
                manner the 5-Star Telemed Marks.
              </p>

              <Title className="section-title" level={3}>
                Contact Information
              </Title>
              <p>
                If you have any questions about these Terms of Use or the 5-Star Telemed Services,
                please visit our{' '}
                <u>
                  <a href="https://www.5startelemed.com/" rel="noopener noreferrer" target="_blank">
                    website
                  </a>
                </u>{' '}
                to contact us.
              </p>
              <p>Sincerely,</p>
              <p>Theoria Medical | 5-Star Telemed</p>
            </div>
          </div>
        </Content>
        <Footer className="footer">
          <p>© 5-Star Telemed 2020</p>
        </Footer>
      </TermsPageWrapper>
    );
  }
}
